/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
// context/SearchContext.tsx - UPDATED VERSION
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  searchService,
  type SearchResponse,
  type SearchSuggestion,
  type SearchTrends,
  type SearchHistoryItem,
} from "../service/searchService";
import { useAuth } from "./AuthContext";

export interface SearchFilters {
  query: string;
  category: string;
  type: string;
  location: string;
  salaryRange: string;
  experienceLevel: string;
  tags: string[];
  datePosted: string;
  searchType: "jobs" | "events" | "articles" | "companies" | "all";
}

export interface SearchResult {
  id: string;
  type: "job" | "event" | "article" | "company" | "user";
  title: string;
  description: string;
  image?: string;
  url: string;
  category?: string;
  tags?: string[];
  date?: string;
  relevance: number;
}

interface SearchContextType {
  // Search state
  searchFilters: SearchFilters;
  searchResults: SearchResult[];
  isSearching: boolean;
  showSearchResults: boolean;
  searchError: string | null;

  // Actions
  updateFilters: (filters: Partial<SearchFilters>) => void;
  performSearch: (
    query?: string,
    filters?: Partial<SearchFilters>,
  ) => Promise<void>;
  clearSearch: () => void;
  setShowSearchResults: (show: boolean) => void;

  // Quick search
  quickSearch: (query: string, type?: SearchFilters["searchType"]) => void;

  // Recent searches
  recentSearches: string[];
  addToRecentSearches: (query: string) => void;
  clearRecentSearches: () => void;

  // API methods
  getSearchSuggestions: (query: string) => Promise<SearchSuggestion[]>;
  getSearchTrends: () => Promise<SearchTrends>;
  getSearchHistory: () => Promise<SearchHistoryItem[]>;
  saveSearchToHistory: (
    query: string,
    filters?: Record<string, any>,
  ) => Promise<void>;
  clearSearchHistory: () => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Default search filters
const defaultFilters: SearchFilters = {
  query: "",
  category: "",
  type: "",
  location: "",
  salaryRange: "",
  experienceLevel: "",
  tags: [],
  datePosted: "",
  searchType: "all",
};

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [searchFilters, setSearchFilters] =
    useState<SearchFilters>(defaultFilters);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.warn(
          "Failed to parse recent searches from localStorage:",
          error,
        );
        setRecentSearches([]);
      }
    }
  }, []);

  // Save recent searches to localStorage
  useEffect(() => {
    localStorage.setItem("recentSearches", JSON.stringify(recentSearches));
  }, [recentSearches]);

  const updateFilters = useCallback((filters: Partial<SearchFilters>) => {
    setSearchFilters((prev) => ({ ...prev, ...filters }));
  }, []);

  const addToRecentSearches = useCallback((query: string) => {
    if (!query.trim()) return;

    setRecentSearches((prev) => {
      const filtered = prev.filter(
        (search) => search.toLowerCase() !== query.toLowerCase(),
      );
      return [query, ...filtered].slice(0, 10);
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
  }, []);

  // Real API search function - WITH ERROR HANDLING
  const performSearch = useCallback(
    async (query?: string, additionalFilters?: Partial<SearchFilters>) => {
      const searchQuery = query || searchFilters.query;
      const filters = {
        ...searchFilters,
        ...additionalFilters,
        query: searchQuery,
      };

      if (!searchQuery.trim()) {
        setSearchResults([]);
        setSearchError(null);
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      setShowSearchResults(true);

      try {
        // Add to recent searches (local only)
        addToRecentSearches(searchQuery);

        // Save to search history if user is authenticated - WITH ERROR HANDLING
        if (user) {
          try {
            await searchService.saveSearch(searchQuery, filters);
          } catch (error) {
            // Silently fail for history saving - don't break the main search
            console.warn("Failed to save search history:", error);
          }
        }

        // Perform actual API search
        const response: SearchResponse =
          await searchService.globalSearch(filters);

        if (response.success) {
          setSearchResults(response.results);
        } else {
          throw new Error("Search failed");
        }
      } catch (error) {
        console.error("Search error:", error);

        // Provide user-friendly error messages
        if (error instanceof Error) {
          if (
            error.message.includes("500") ||
            error.message.includes("Internal Server Error")
          ) {
            setSearchError(
              "Search service is temporarily unavailable. Please try again later.",
            );
          } else {
            setSearchError(error.message);
          }
        } else {
          setSearchError(
            "Failed to perform search. Please check your connection and try again.",
          );
        }

        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [searchFilters, addToRecentSearches, user],
  );

  const quickSearch = useCallback(
    (query: string, type: SearchFilters["searchType"] = "all") => {
      updateFilters({ query, searchType: type });
      performSearch(query);
    },
    [updateFilters, performSearch],
  );

  const clearSearch = useCallback(() => {
    setSearchFilters(defaultFilters);
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchError(null);
  }, []);

  // API methods with better error handling
  const getSearchSuggestions = useCallback(
    async (query: string): Promise<SearchSuggestion[]> => {
      if (!query.trim() || query.trim().length < 2) {
        return [];
      }

      try {
        const response = await searchService.getSearchSuggestions(query);
        return response.success ? response.suggestions : [];
      } catch (error) {
        console.error("Failed to get search suggestions:", error);
        // Return empty array instead of throwing
        return [];
      }
    },
    [],
  );

  const getSearchTrends = useCallback(async (): Promise<SearchTrends> => {
    try {
      const response = await searchService.getSearchTrends();
      return response.success
        ? response.trends
        : {
            popularCategories: [],
            popularLocations: [],
            popularCompanies: [],
            jobTypes: [],
            updatedAt: new Date().toISOString(),
          };
    } catch (error) {
      console.error("Failed to get search trends:", error);
      return {
        popularCategories: [],
        popularLocations: [],
        popularCompanies: [],
        jobTypes: [],
        updatedAt: new Date().toISOString(),
      };
    }
  }, []);

  const getSearchHistory = useCallback(async (): Promise<
    SearchHistoryItem[]
  > => {
    if (!user) {
      return []; // Return empty if user not authenticated
    }

    try {
      const response = await searchService.getSearchHistory();
      return response.success ? response.history : [];
    } catch (error) {
      console.error("Failed to get search history:", error);
      return [];
    }
  }, [user]);

  const saveSearchToHistory = useCallback(
    async (query: string, filters: Record<string, any> = {}) => {
      if (!user) {
        return; // Don't save if user not authenticated
      }

      try {
        await searchService.saveSearch(query, filters);
      } catch (error) {
        console.error("Failed to save search history:", error);
      }
    },
    [user],
  );

  const clearSearchHistory = useCallback(async () => {
    if (!user) {
      return; // Don't clear if user not authenticated
    }

    try {
      await searchService.clearSearchHistory();
      // Also clear local recent searches
      clearRecentSearches();
    } catch (error) {
      console.error("Failed to clear search history:", error);
    }
  }, [user, clearRecentSearches]);

  const value: SearchContextType = {
    searchFilters,
    searchResults,
    isSearching,
    showSearchResults,
    searchError,
    updateFilters,
    performSearch,
    clearSearch,
    setShowSearchResults,
    quickSearch,
    recentSearches,
    addToRecentSearches,
    clearRecentSearches,
    getSearchSuggestions,
    getSearchTrends,
    getSearchHistory,
    saveSearchToHistory,
    clearSearchHistory,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};
